import React, { useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import LoginBcgImage from "../../assets/images/bg-login.png";
import BgForgotImage from "../../assets/images/forgot-bg.png";
import Welcome from "../../common/components/welcome/welcome";
import "../login/login.scss";
import { NavLink, useNavigate } from "react-router-dom";
import { routes } from "../../constants/routes";
import { useSetLoading } from "../../common/hooks/appearance";
import { Box } from "@mui/material";
import "./contact-us.scss";
import DisplayVersion from "../display-version/display-version";
import { CountryCodeWithPhoneNumber } from "../../common/components";
import icons from "../../assets/icons/icons";
import { emailValidationRegex } from "../../constants/regexStrings";
import { isOnlyCountryCode } from "../../utils/utils";
import phoneValidation from "../../common/components/phone-number-input-component/phone-validation";
import { contactUs } from "../../api/login";
import backIcon from "../../assets/icons/back.svg";

const ContactUs = () => {
  const [message, setMessage] = useState<{
    message: string;
    types: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const navigate = useNavigate();

  const initialValues = {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    companyName: "",
  };

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .matches(emailValidationRegex, "Please enter a valid Email address")
      .required("Email is required"),
    companyName: Yup.string().required("Company name is required"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .test(
        "valid-phone-check",
        "Please enter a valid phone number",
        function (value) {
          if (!value) return false;

          if (isOnlyCountryCode(value)) {
            return this.createError({
              message: "Phone number is required",
            });
          }

          if (!phoneValidation(value)) {
            return this.createError({
              message: "Please enter a valid phone number",
            });
          }

          return true;
        }
      ),
  });

  const onSubmit = async (values: typeof initialValues) => {
    setIsLoading(true);
    try {
      const response: any = await contactUs(values);
      if (response?.data?.data?.message) {
        setIsSubmitted(true);
      }
      setIsLoading(false);
    } catch (error: any) {
      setMessage({
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong!",
        types: "error",
      });
      setIsLoading(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div
      className="login-page-wrapper contact-us-wrapper"
      style={{
        backgroundImage: `url(${BgForgotImage}), url(${LoginBcgImage})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "center bottom, center bottom",
        backgroundSize: "100% 40%, 100% 40%",
      }}
    >
      <div className="login-card-wrapper">
        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ values, dirty, isValid, setFieldValue, setFieldTouched }) => {
              return (
                <Form className="loginForm">
                  {isSubmitted ? (
                    <Box className="forgotpassword-wrapper">
                      <div className="back-icon-wraper">
                        <NavLink to={routes.LOGIN}>
                          <img src={backIcon} alt="back" />
                          <span>Back to Login</span>
                        </NavLink>
                      </div>
                      <Box className="success-wrapper">
                        <Box sx={{ position: "relative" }}>
                          <img
                            src={icons.ContactUsLike}
                            style={{
                              position: "absolute",
                              top: "65px",
                              left: "115px",
                            }}
                          />
                          <img
                            src={icons.ContactUsStars}
                            className="stars-image"
                          />
                        </Box>
                        <p className="su">
                          Thank you for contacting us. Our team will get back to
                          you shortly.
                        </p>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <h2>Contact Us</h2>

                      <div className="formBody">
                        <Box className="name-wrapper" gap={4}>
                          <div className="field">
                            <Field
                              type="text"
                              name="firstName"
                              placeholder="First Name"
                              onChange={(e: any) => {
                                const lettersOnly = e.target.value.replace(
                                  /[^a-zA-Z0-9 ]/g,
                                  ""
                                );
                                setFieldValue("firstName", lettersOnly);
                              }}
                            />
                            <ErrorMessage
                              name="firstName"
                              component="div"
                              className="errorMessage"
                            />
                          </div>
                          <div className="field">
                            <Field
                              type="text"
                              name="lastName"
                              placeholder="Last Name "
                              onChange={(e: any) => {
                                const lettersOnly = e.target.value.replace(
                                  /[^a-zA-Z0-9 ]/g,
                                  ""
                                );
                                setFieldValue("lastName", lettersOnly);
                              }}
                            />
                            <ErrorMessage
                              name="lastName"
                              component="div"
                              className="errorMessage"
                            />
                          </div>
                        </Box>
                        <div className="field">
                          <Field
                            type="email"
                            name="email"
                            placeholder="Email"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="errorMessage"
                          />
                        </div>

                        <div className="field">
                          <CountryCodeWithPhoneNumber
                            fieldName="phoneNumber"
                            placeholder="Enter phone number"
                            onChange={(value: string, country: any) => {
                              const countryCode = country?.dialCode;

                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object"
                                );
                                return;
                              }

                              const numberOnly = value.slice(
                                countryCode.length
                              );
                              const formatted = `+${countryCode} ${numberOnly}`;

                              setFieldValue("phoneNumber", formatted);
                            }}
                            onBlur={() => {
                              setFieldTouched("phoneNumber", true);
                            }}
                            value={values?.phoneNumber || ""}
                            defaultCountry="us"
                            // countryCodeEditable
                          />
                          <ErrorMessage
                            name="phoneNumber"
                            component="div"
                            className="errorMessage"
                          />
                        </div>

                        <div className="field">
                          <Field
                            type="text"
                            name="companyName"
                            placeholder="Company Name"
                          />
                          <ErrorMessage
                            name="companyName"
                            component="div"
                            className="errorMessage"
                          />
                        </div>

                        <Box className="button-wrapper" gap={1}>
                          <button
                            type="button"
                            className="cancelButton"
                            onClick={() => {
                              navigate(routes.LOGIN);
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="submitButton"
                            disabled={!dirty || !isValid}
                          >
                            {isLoading ? "Sending..." : "Submit"}
                          </button>
                        </Box>

                        {message?.message && (
                          <div
                            className={`${
                              message?.types === "error"
                                ? "login-error-message"
                                : "login-success-message"
                            } message-container`}
                            style={{ marginTop: "10px", textAlign: "center" }}
                          >
                            {message?.message}
                          </div>
                        )}
                        <div className="version-info">
                          <DisplayVersion />
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className="right-content-wraper">
          <Welcome />
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
